import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ProfilePhotoUploader } from '../components/ProfilePhotoUploader';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import SafeChart from '../components/SafeChart';
import CohortCard from '../components/CohortCard';
import ClearanceBadge from '../components/ClearanceBadge';
import SeasonTimeline from '../components/SeasonTimeline';
import Athlete360AcademicSummary from '../components/academic/Athlete360AcademicSummary';
import { useCurrentAthlete } from '../contexts/AthleteContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import {
  Activity, Target, GraduationCap, Heart, FileText,
  ChevronRight, Swords, Dumbbell, ShieldAlert, BookOpen,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  Download, ArrowLeft, Flag, Plus, Trash2, ExternalLink, Clock, Link2, Settings,
  XCircle, ThumbsUp, ThumbsDown, Minus, Loader2, Lock,
  Scale, Gauge, Apple, Brain, Award, FileSignature, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Phase 79 — event icon + colour registry used by the new filterable
// Timeline tab. Mirror of the backend `TIMELINE_REGISTRY` types — adding
// a new module here means adding ONE row.
const EVENT_STYLE = {
  match:      { icon: Swords,        color: 'text-blue-600',    bg: 'bg-blue-50',    border: 'border-blue-200',    label: 'Matches' },
  training:   { icon: Dumbbell,      color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Training' },
  injury:     { icon: ShieldAlert,   color: 'text-red-600',     bg: 'bg-red-50',     border: 'border-red-200',     label: 'Injury' },
  pdp:        { icon: Target,        color: 'text-purple-600',  bg: 'bg-purple-50',  border: 'border-purple-200',  label: 'PDP' },
  academic:   { icon: BookOpen,      color: 'text-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-200',   label: 'Academic' },
  fitness:    { icon: Gauge,         color: 'text-indigo-600',  bg: 'bg-indigo-50',  border: 'border-indigo-200',  label: 'Assessments' },
  biometrics: { icon: Scale,         color: 'text-teal-600',    bg: 'bg-teal-50',    border: 'border-teal-200',    label: 'Biometrics' },
  nutrition:  { icon: Apple,         color: 'text-lime-600',    bg: 'bg-lime-50',    border: 'border-lime-200',    label: 'Nutrition' },
  mental:     { icon: Brain,         color: 'text-pink-600',    bg: 'bg-pink-50',    border: 'border-pink-200',    label: 'Mental' },
  career:     { icon: Award,         color: 'text-orange-600',  bg: 'bg-orange-50',  border: 'border-orange-200',  label: 'Career' },
  documents:  { icon: FileSignature, color: 'text-slate-600',   bg: 'bg-slate-50',   border: 'border-slate-200',   label: 'Documents' },
  ai:         { icon: Sparkles,      color: 'text-violet-600',  bg: 'bg-violet-50',  border: 'border-violet-200',  label: 'AI' },
};

const Athlete360Dashboard = () => {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const { user, hasModuleAccess } = useAuth();
  const token = localStorage.getItem('token');
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [flags, setFlags] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  const [internalNotes, setInternalNotes] = useState([]);

  const isStaff = user?.role === 'Admin' || user?.role === 'Staff';
  const { setCurrentAthlete } = useCurrentAthlete();

  const fetchData = useCallback(async () => {
    if (!athleteId || !token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const requests = [
        axios.get(`${API}/api/athletes/${athleteId}/360`, { headers }),
        axios.get(`${API}/api/athletes/${athleteId}/timeline?limit=25`, { headers }),
        axios.get(`${API}/api/flags/athlete/${athleteId}`, { headers }),
        axios.get(`${API}/api/reports/season/history?athlete_id=${athleteId}`, { headers }),
      ];
      if (isStaff) {
        requests.push(
          axios
            .get(`${API}/api/injury-recovery/notes/athlete/${athleteId}?limit=5`, { headers })
            .catch(() => ({ data: [] }))
        );
      }
      const results = await Promise.all(requests);
      const [dashRes, timelineRes, flagsRes, reportsRes, notesRes] = results;
      setData(dashRes.data);
      setTimeline(timelineRes.data || []);
      setFlags(flagsRes.data || []);
      setSavedReports(reportsRes.data || []);
      setInternalNotes(notesRes?.data || []);
      // Persist current-athlete context for staff/admin flows.
      if (isStaff && dashRes.data?.athlete?.id) {
        setCurrentAthlete(dashRes.data.athlete);
      }
    } catch (err) {
      console.error('Failed to load 360 data:', err);
    } finally {
      setLoading(false);
    }
  }, [athleteId, token, isStaff, setCurrentAthlete]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) {
    return (
      <Layout>
      <div className="space-y-4">
        <div className="h-28 rounded-xl bg-gray-100 dark:bg-gray-700 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[1,2,3].map(i => <div key={i} className="h-40 rounded-xl bg-gray-100 dark:bg-gray-700 animate-pulse" />)}
        </div>
      </div>
      </Layout>
    );
  }

  const athlete = data?.athlete || {};
  const training = data?.training || { attendance_rate: null, present_count: 0, absent_count: 0 };
  const performance = data?.performance || { total_matches: 0, total_goals: 0, total_assists: 0 };
  const medical = data?.medical || { has_medical_record: false, total_injuries: 0, active_injuries: 0 };
  const academic = data?.academic || { has_academic_profile: false };
  const documents = data?.documents || { total_documents: 0, by_category: {} };
  const snapshot = data?.performance_snapshot || {};

  const isInjured = snapshot?.injury_status === 'Injured' || snapshot?.injury_status === 'In Recovery' || athlete?.status === 'injured';
  const matchRatings = snapshot?.last_match_ratings || [];
  const avgRating = matchRatings.length > 0 ? (matchRatings.reduce((s, m) => s + (m.rating || 0), 0) / matchRatings.length).toFixed(1) : null;

  const ratingTrend = [...matchRatings].reverse().map(m => ({
    date: m.date?.substring(5) || '',
    rating: m.rating || 0,
    opponent: m.opponent || ''
  }));

  // Rule-based insights
  const insights = [];
  if (ratingTrend.length >= 2) {
    const lastTwo = ratingTrend.slice(-2);
    if (lastTwo[1].rating > lastTwo[0].rating) insights.push({ type: 'positive', icon: TrendingUp, text: 'Match ratings improving' });
    if (lastTwo[1].rating < lastTwo[0].rating - 1) insights.push({ type: 'warning', icon: TrendingDown, text: 'Match performance declining' });
  }
  if (training.attendance_rate < 75 && training.attendance_rate > 0) insights.push({ type: 'warning', icon: AlertTriangle, text: `Attendance low (${training.attendance_rate}%)` });
  if (training.attendance_rate >= 90) insights.push({ type: 'positive', icon: CheckCircle2, text: `Excellent attendance (${training.attendance_rate}%)` });
  if (isInjured) insights.push({ type: 'danger', icon: ShieldAlert, text: 'Currently injured' });
  if (snapshot?.pdp_progress >= 70) insights.push({ type: 'positive', icon: Target, text: `Strong PDP progress (${snapshot.pdp_progress?.toFixed(0)}%)` });
  if (snapshot?.pdp_progress > 0 && snapshot?.pdp_progress < 30) insights.push({ type: 'warning', icon: Target, text: `PDP needs attention (${snapshot.pdp_progress?.toFixed(0)}%)` });

  const handleExportPDF = async () => {
    try {
      const res = await axios.get(`${API}/api/reports/athlete/${athleteId}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `PFAP_Report_${(athlete?.full_name || 'Athlete').replace(/ /g, '_')}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  // Phase 82 — module → permission key map. Used by both `quickNav` (to
  // block navigation when access is denied) and the OverviewTab cards (to
  // gate sensitive data behind a "Restricted" empty state).
  const MODULE_PERMISSION_KEY = {
    'match-reports':      'match_reports',
    'training':           'training',
    'pdp':                'pdp',
    'academic':           'academic',
    'medical':            'injury_recovery',
    'documents':          'documents',
    'finance':            'finance',
    'injury-recovery':    'injury_recovery',
    'biometrics':         'biometrics',
    'fitness-assessments':'fitness_assessment',
    'nutrition':          'training_programs.nutrition',
    'mental-hub':         'mental_hub',
    'career-pathway':     'career_pathway',
  };

  const quickNav = (path) => {
    // Phase 82 — Permission gate. If the user lacks view access to the
    // target module, refuse navigation (the card will already be
    // rendered as Restricted so this is the belt-and-braces guard).
    const permKey = MODULE_PERMISSION_KEY[path];
    if (permKey && !hasModuleAccess(permKey, 'view')) {
      toast.error('You do not have permission to view this module.');
      return;
    }
    // Map module names to athlete-scoped route segments (these DO have
    // dedicated per-athlete pages registered in App.js).
    const athleteRouteMap = {
      'match-reports': 'performance',
      'training': 'training',
      'pdp': 'pdp',
      'academic': 'academic',
      'medical': 'health',
      'documents': 'documents',
      'finance': 'finance',
      'injury-recovery': 'injury',
    };
    // Phase 79 — module pages that support `?athlete=<id>` pre-selection.
    // No nested per-athlete route exists for these; we open the GLOBAL
    // module with the athlete already selected via query string.
    const globalModuleWithAthlete = {
      'biometrics':         '/biometrics',
      'fitness-assessments':'/assessments',
      'nutrition':          '/performance-programs',
      'mental-hub':         '/mental-hub',
      'career-pathway':     '/career-pathway',
    };
    // Injury Recovery: always the global page with ?athlete_id=
    if (path === 'injury-recovery') {
      navigate(`/injury-recovery?athlete_id=${athleteId}`);
      return;
    }
    if (path in globalModuleWithAthlete) {
      navigate(`${globalModuleWithAthlete[path]}?athlete=${athleteId}`);
      return;
    }
    if (user?.role === 'Player' || user?.role === 'Parent') {
      navigate(`/my-${path}`);
      return;
    }
    const segment = athleteRouteMap[path] || path;
    navigate(`/athletes/${athleteId}/${segment}`);
  };

  return (
    <Layout>
    <div className="space-y-4" data-testid="athlete-360-dashboard">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:text-gray-100 h-8">
          <ArrowLeft size={15} className="mr-1.5" /> Back
        </Button>
        <div className="flex items-center gap-2">
          {(user?.role === 'Admin' || user?.role === 'Staff') && (
            <Button size="sm" variant="outline" onClick={() => navigate(`/athletes/${athleteId}/manage`)} className="h-8" data-testid="manage-athlete-btn">
              <Settings size={14} className="mr-1.5" /> Manage
            </Button>
          )}
          <Button size="sm" onClick={handleExportPDF} className="bg-emerald-600 hover:bg-emerald-500 text-white h-8" data-testid="export-pdf-btn">
            <Download size={14} className="mr-1.5" /> Export PDF
          </Button>
        </div>
      </div>

      {/* Summary Header */}
      <Card className="bg-white dark:bg-gray-800 shadow-sm" data-testid="summary-header">
        <CardContent className="p-4 md:p-5">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <ProfilePhotoUploader
                athleteId={athleteId}
                athleteName={athlete?.full_name}
                currentPhotoUrl={athlete?.profile_photo}
                size="md"
                canManage={user?.role === 'Admin' || user?.role === 'Staff'}
                onChange={(url) => setData(prev => prev ? { ...prev, athlete: { ...(prev.athlete || {}), profile_photo: url } } : prev)}
                testIdPrefix="athlete-360-photo"
              />
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate" data-testid="athlete-name">{athlete?.full_name}</h1>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <Badge className="text-[10px] px-1.5 py-0 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500">{athlete?.position || 'N/A'}</Badge>
                  <span className="text-xs text-gray-400 dark:text-gray-500">{athlete?.squad}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500" data-testid="athlete-gender">
                    Gender: {athlete?.gender || '—'}
                  </span>
                  <ClearanceBadge value={isInjured ? 'Injured' : 'Fit'} size="sm" testId="athlete-clearance-badge" />
                </div>
                {athlete?.staff_names?.length > 0 && (
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1" data-testid="assigned-staff-names">
                    Staff: {athlete.staff_names.join(', ')}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4 md:gap-6 flex-wrap" data-testid="kpi-strip">
              <KPIBadge label="PDP" value={snapshot?.pdp_progress ? `${snapshot.pdp_progress.toFixed(0)}%` : '--'} color="purple" />
              <KPIBadge label="Attendance" value={Number.isFinite(training?.attendance_rate) ? `${training.attendance_rate}%` : '--'} color="emerald" />
              <KPIBadge label="Avg Rating" value={avgRating || '--'} color="blue" />
              <KPIBadge label="Career" value={snapshot?.career_phase || '--'} color="amber" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Insights */}
      {insights.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="insights-panel">
          {insights.map((ins, i) => {
            const Icon = ins.icon;
            const colors = { positive: 'bg-emerald-50 text-emerald-700 border-emerald-200', warning: 'bg-amber-50 text-amber-700 border-amber-200', danger: 'bg-red-50 text-red-700 border-red-200' };
            return (
              <div key={i} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs ${colors[ins.type]}`}>
                <Icon size={13} /> {ins.text}
              </div>
            );
          })}
        </div>
      )}

      {/* Active Flags */}
      {flags.filter(f => f.status === 'Active').length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="athlete-active-flags">
          {flags.filter(f => f.status === 'Active').map((f) => {
            const sevColor = f.severity === 'High' ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800/40' : f.severity === 'Medium' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800/40' : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800/40';
            return (
              <div key={f.id} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs ${sevColor}`} data-testid={`athlete-flag-${f.id}`}>
                <Flag size={12} /> {f.flag_type} <Badge className={`text-[9px] px-1 py-0 ${sevColor}`}>{f.severity}</Badge>
              </div>
            );
          })}
          {(user?.role === 'Admin' || user?.role === 'Staff') && (
            <button onClick={() => navigate('/alerts-flags')} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-400 hover:border-pfap-green dark:hover:border-pfap-sage/40 hover:text-pfap-green dark:hover:text-pfap-sage transition-colors" data-testid="manage-flags-btn">
              Manage Flags <ChevronRight size={12} />
            </button>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700 overflow-x-auto pb-px" data-testid="tab-nav">
        {(isStaff ? ['overview', 'timeline', 'trends', 'reports', 'ai-decisions'] : ['overview', 'timeline', 'trends', 'reports']).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
              activeTab === tab ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-500'
            }`} data-testid={`tab-${tab}`}>
            {tab === 'ai-decisions' ? 'AI Decisions' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <OverviewTab athleteId={athleteId} training={training} performance={performance} medical={medical} academic={academic} documents={documents} snapshot={snapshot} navigate={navigate} quickNav={quickNav} user={user} hasModuleAccess={hasModuleAccess} internalNotes={internalNotes} biometricsSummary={data?.biometrics_summary} fitnessSummary={data?.fitness_summary} nutritionSummary={data?.nutrition_summary} mentalSummary={data?.mental_summary} careerSummary={data?.career_summary} injurySummary={data?.injury_summary} />}
      {activeTab === 'timeline' && <TimelineTab events={timeline} navigate={navigate} athleteId={athleteId} />}
      {activeTab === 'trends' && <TrendsTab ratingTrend={ratingTrend} snapshot={snapshot} />}
      {activeTab === 'reports' && <ReportsTab reports={savedReports} athleteId={athleteId} navigate={navigate} user={user} token={token} onRefresh={fetchData} />}
      {activeTab === 'ai-decisions' && isStaff && <AIDecisionsTab athleteId={athleteId} token={token} user={user} />}
    </div>
    </Layout>
  );
};

const KPIBadge = ({ label, value, color }) => {
  const colors = { emerald: 'text-emerald-600', blue: 'text-blue-600', purple: 'text-purple-600', amber: 'text-amber-600' };
  return (
    <div className="text-center">
      <p className={`text-base font-bold ${colors[color] || 'text-gray-900 dark:text-gray-100'}`}>{value}</p>
      <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider">{label}</p>
    </div>
  );
};

const HealthCardContent = ({ athleteId, medical, navigate, user }) => {
  const latest = medical?.latest_active_injury;
  const phase = medical?.current_recovery_phase;
  const openInjury = (e) => {
    e?.stopPropagation?.();
    // Players/Parents land on the global page filtered to themselves.
    if (user?.role === 'Player' || user?.role === 'Parent') {
      navigate(`/injury-recovery?athlete_id=${athleteId}`);
    } else {
      navigate(`/athletes/${athleteId}/injury`);
    }
  };

  return (
    <div className="space-y-2" data-testid="athlete-360-health-card">
      <div className="grid grid-cols-2 gap-3">
        <MiniStat label="Injuries" value={medical?.total_injuries || 0} />
        <MiniStat
          label="Active"
          value={medical?.active_injuries || 0}
          highlight={(medical?.active_injuries || 0) > 0 ? 'red' : null}
        />
      </div>

      {latest ? (
        <div
          className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-2 space-y-1"
          data-testid="active-injury-summary"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-medium text-red-700 dark:text-red-300 truncate">
              {latest.injury_type || 'Injury'}{latest.body_area ? ` · ${latest.body_area}` : ''}
            </div>
            {latest.severity && (
              <Badge className="text-[9px] px-1.5 py-0 bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border border-red-300">
                {latest.severity}
              </Badge>
            )}
          </div>
          <div className="flex items-center justify-between text-[10px] text-red-600/80 dark:text-red-300/80">
            <span>Status: {latest.status || '—'}</span>
            {phase?.phase_name && (
              <span data-testid="recovery-phase-summary">
                {phase.phase_name} ({(phase.phases_completed ?? 0)}/{phase.phases_total ?? 0})
              </span>
            )}
          </div>
        </div>
      ) : (
        <p className="text-[11px] text-gray-500 dark:text-gray-400" data-testid="active-injury-empty">
          No active injury for this athlete.
        </p>
      )}

      <Button
        size="sm"
        variant="outline"
        onClick={openInjury}
        className="w-full h-7 text-[11px] gap-1.5"
        data-testid="open-injury-recovery-btn"
      >
        <ShieldAlert className="w-3 h-3" /> Open Injury &amp; Recovery
      </Button>
    </div>
  );
};

const OverviewTab = ({ athleteId, training, performance, medical, academic, documents, snapshot, navigate, quickNav, user, hasModuleAccess, internalNotes, biometricsSummary, fitnessSummary, nutritionSummary, mentalSummary, careerSummary, injurySummary }) => {
  const matchRatings = snapshot?.last_match_ratings || [];
  const isStaff = user?.role === 'Admin' || user?.role === 'Staff';
  const biom = biometricsSummary?.latest || {};
  const fit = fitnessSummary?.latest || {};
  const nutri = nutritionSummary?.active_plan || nutritionSummary?.latest_plan || {};
  const mentalLatest = mentalSummary?.latest || {};
  const career = careerSummary?.current_phase || {};
  const injuryOpen = injurySummary?.latest_open || {};

  // Phase 82 — Permission gate per card. `hasModuleAccess` falls through
  // to true for Admin so admins always see live data. For non-Admins we
  // check the user's effective view-level permission for the module.
  // `hasAccess` is a safe wrapper in case the prop is missing (legacy
  // callers / loading state).
  const hasAccess = (module) =>
    typeof hasModuleAccess === 'function' ? hasModuleAccess(module, 'view') : true;
  const can = {
    match_reports:   hasAccess('match_reports'),
    training:        hasAccess('training'),
    pdp:             hasAccess('pdp'),
    injury_recovery: hasAccess('injury_recovery'),
    academic:        hasAccess('academic'),
    documents:       hasAccess('documents'),
    biometrics:      hasAccess('biometrics'),
    fitness:         hasAccess('fitness_assessment'),
    nutrition:       hasAccess('training_programs.nutrition') || hasAccess('training_programs'),
    mental_hub:      hasAccess('mental_hub'),
    career_pathway:  hasAccess('career_pathway'),
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="overview-tab">
      <CohortCard athleteId={athleteId} />
      <SeasonTimeline athleteId={athleteId} canEdit={isStaff} />
      <StatCard title="Performance" icon={Activity} color="blue" onClick={can.match_reports ? () => quickNav('match-reports') : undefined} testid="overview-card-performance">
        {!can.match_reports ? (
          <RestrictedBody moduleLabel="Match Reports" testid="overview-card-performance-restricted-body" />
        ) : matchRatings.length > 0 ? (
          <div className="space-y-2">
            {matchRatings.slice(0, 3).map((m, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-500 truncate flex-1">{m.opponent}</span>
                <RatingBadge rating={m.rating} />
              </div>
            ))}
          </div>
        ) : <EmptyState text="No match data" />}
        {can.match_reports && (
          <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex justify-between text-[10px] text-gray-400 dark:text-gray-500">
            <span>Goals: {performance?.total_goals || 0}</span>
            <span>Assists: {performance?.total_assists || 0}</span>
            <span>Matches: {performance?.total_matches || matchRatings.length}</span>
          </div>
        )}
        {isStaff && can.match_reports && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(`/match-reports?athlete=${athleteId}`); }}
            className="mt-2 w-full text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 text-left"
            data-testid="view-match-history-link"
          >
            View Match History →
          </button>
        )}
      </StatCard>

      <StatCard title="Training" icon={Dumbbell} color="emerald" onClick={can.training ? () => quickNav('training') : undefined} testid="overview-card-training">
        {!can.training ? (
          <RestrictedBody moduleLabel="Training" testid="overview-card-training-restricted-body" />
        ) : (
        <div className="space-y-2">
          <div className="flex justify-between items-end">
            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100" data-testid="attendance-rate-value">
              {Number.isFinite(training?.attendance_rate) ? `${training.attendance_rate}%` : '—'}
            </span>
            <span className="text-[10px] text-gray-400 dark:text-gray-500" data-testid="assigned-sessions-label">
              from {training?.marked_sessions ?? 0} finalized marks
            </span>
          </div>
          <Progress value={Number.isFinite(training?.attendance_rate) ? training.attendance_rate : 0} className="h-1.5" />
          <div className="flex justify-between text-[10px] text-gray-400 dark:text-gray-500">
            <span>{training?.attended ?? training?.attended_or_justified ?? training?.present_count ?? 0} attended</span>
            <span>{training?.absent_or_no_show ?? training?.absent_count ?? 0} not attended</span>
            <span>{Number.isFinite(training?.availability_rate) ? `${training.availability_rate}% full availability` : '— full availability'}</span>
            {(training?.pending_or_unmarked || training?.unmarked_sessions || 0) > 0 && (
              <span className="text-amber-600 dark:text-amber-400" data-testid="unmarked-pending-badge">
                {training.pending_or_unmarked || training.unmarked_sessions} pending
              </span>
            )}
          </div>
        </div>
        )}
      </StatCard>

      <StatCard title="Development Plan" icon={Target} color="purple" onClick={can.pdp ? () => quickNav('pdp') : undefined} testid="overview-card-pdp">
        {!can.pdp ? (
          <RestrictedBody moduleLabel="Development Plan" testid="overview-card-pdp-restricted-body" />
        ) : snapshot?.pdp_progress > 0 ? (
          <div className="space-y-2">
            <div className="flex justify-between items-end">
              <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{snapshot.pdp_progress.toFixed(0)}%</span>
              <span className="text-[10px] text-gray-400 dark:text-gray-500">{snapshot?.active_goals_count || 0} active goals</span>
            </div>
            <Progress value={snapshot.pdp_progress} className="h-1.5" />
          </div>
        ) : <EmptyState text="No PDP goals" />}
      </StatCard>

      {/* Phase 79 — Health renamed to Injury Record. Surfaces active injuries,
          rehab plans, medical alerts and clearance status. */}
      <StatCard title="Injury Record" icon={ShieldAlert} color="red" onClick={can.injury_recovery ? () => quickNav('injury-recovery') : undefined} testid="overview-card-injury">
        {!can.injury_recovery ? (
          <RestrictedBody moduleLabel="Injury & Recovery" testid="overview-card-injury-restricted-body" />
        ) : (
        <>
        <div className="space-y-1.5 text-xs">
          <div className="flex justify-between">
            <span className="text-gray-400 dark:text-gray-500">Active injuries</span>
            <span className={`font-semibold ${(injurySummary?.active_injuries || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="injury-active-count">
              {injurySummary?.active_injuries || 0}
            </span>
          </div>
          {injuryOpen.injury_type && (
            <div className="text-[11px] text-gray-600 dark:text-gray-400 truncate">
              Latest: {injuryOpen.injury_type}{injuryOpen.body_area ? ` (${injuryOpen.body_area})` : ''}
            </div>
          )}
          <div className="pt-1.5 mt-1.5 border-t border-gray-100 dark:border-gray-700">
            <HealthCardContent athleteId={athleteId} medical={medical} navigate={navigate} user={user} />
          </div>
        </div>
        {isStaff && internalNotes && internalNotes.length > 0 && (
          <div
            className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800"
            data-testid="internal-notes-section"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-1">Recent internal notes</p>
            <ul className="space-y-1">
              {internalNotes.slice(0, 3).map((n) => (
                <li key={n.id} className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2" title={n.content}>
                  <span className="font-medium text-gray-800 dark:text-gray-200">{n.author_name || 'Staff'}:</span> {n.content}
                </li>
              ))}
            </ul>
          </div>
        )}
        </>
        )}
      </StatCard>

      <StatCard title="Academic" icon={GraduationCap} color="amber" onClick={can.academic ? () => quickNav('academic') : undefined} testid="overview-card-academic">
        {!can.academic ? (
          <RestrictedBody moduleLabel="Academic" testid="overview-card-academic-restricted-body" />
        ) : academic?.has_academic_profile ? (
          <div className="space-y-1.5 text-xs">
            {/* Show ALL programmes the athlete is enrolled in (Phase 81
                fix: previous version only displayed school/grade/GPA from
                the first profile which collapsed multi-programme athletes
                into a misleading single-row summary). */}
            {(academic?.profiles && academic.profiles.length > 0) ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 dark:text-gray-500">Programmes</span>
                  <span className="text-gray-700 dark:text-gray-300" data-testid="academic-programme-count">
                    {academic.profiles.length}
                  </span>
                </div>
                <ul className="space-y-1">
                  {academic.profiles.slice(0, 3).map((p, i) => (
                    <li key={p.id || i} className="flex justify-between items-baseline gap-2 border-t border-gray-100 dark:border-gray-700 pt-1 first:border-t-0 first:pt-0">
                      <span className="text-gray-700 dark:text-gray-300 truncate flex-1" data-testid={`academic-programme-name-${i}`}>
                        {p.name || p.programme_name || p.school_name || 'Programme'}
                      </span>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 shrink-0" data-testid={`academic-programme-status-${i}`}>
                        {p.status || '—'}
                        {p.latest_grade ? ` · ${p.latest_grade}` : (p.latest_avg_grade ? ` · ${p.latest_avg_grade}` : '')}
                      </span>
                    </li>
                  ))}
                </ul>
                {academic.profiles.length > 3 && (
                  <p className="text-[10px] text-gray-500 dark:text-gray-500">+{academic.profiles.length - 3} more</p>
                )}
                {academic?.latest_report?.created_at && (
                  <p className="text-[10px] text-gray-500 dark:text-gray-500 pt-1 border-t border-gray-100 dark:border-gray-700">
                    Latest report: {new Date(academic.latest_report.created_at).toLocaleDateString()}
                    {academic.latest_report.period ? ` · ${academic.latest_report.period}` : ''}
                  </p>
                )}
              </>
            ) : (
              // Single-programme legacy data path — fall back to the
              // school/grade/GPA snapshot but ONLY when GPA is actually
              // populated. Empty GPA/grade rows used to render as "--"
              // which looked like incorrect data; suppress them now.
              <>
                {academic?.school_name && (
                  <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">School</span><span className="text-gray-700 dark:text-gray-300 truncate ml-2">{academic.school_name}</span></div>
                )}
                {academic?.grade_level && (
                  <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Grade</span><span className="text-gray-700 dark:text-gray-300">{academic.grade_level}</span></div>
                )}
                {academic?.latest_gpa && (
                  <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">GPA</span><span className="text-gray-700 dark:text-gray-300">{academic.latest_gpa}</span></div>
                )}
                {!academic?.school_name && !academic?.grade_level && !academic?.latest_gpa && (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">Academic profile on file (no detail available).</p>
                )}
              </>
            )}
          </div>
        ) : <EmptyState text="No academic records yet." />}
      </StatCard>

      <StatCard title="Documents" icon={FileText} color="slate" onClick={can.documents ? () => quickNav('documents') : undefined} testid="overview-card-documents">
        {!can.documents ? (
          <RestrictedBody moduleLabel="Documents" testid="overview-card-documents-restricted-body" />
        ) : (
        <>
        <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{documents?.total_documents || 0}</span>
        {documents?.by_category && Object.keys(documents.by_category).length > 0 ? (
          <div className="flex flex-wrap gap-1 mt-1">
            {Object.entries(documents.by_category).map(([cat, count]) => (
              <Badge key={cat} className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-500">{cat}: {count}</Badge>
            ))}
          </div>
        ) : <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">No documents</p>}
        </>
        )}
      </StatCard>

      {/* Phase 79+80 — Biometrics summary card now surfaces the
          rules-based benchmark engine output (body profile + action focus). */}
      <StatCard title="Biometrics" icon={Scale} color="teal" onClick={can.biometrics ? () => quickNav('biometrics') : undefined} testid="overview-card-biometrics">
        {!can.biometrics ? (
          <RestrictedBody moduleLabel="Biometrics" testid="overview-card-biometrics-restricted-body" />
        ) : biom.date ? (
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Last assessment</span><span className="text-gray-700 dark:text-gray-300">{biom.date}</span></div>
            <div className="grid grid-cols-2 gap-x-3">
              {biom.weight_kg !== null && biom.weight_kg !== undefined && <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Weight</span><span className="text-gray-700 dark:text-gray-300">{biom.weight_kg} kg</span></div>}
              {biom.height_cm !== null && biom.height_cm !== undefined && <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Height</span><span className="text-gray-700 dark:text-gray-300">{biom.height_cm} cm</span></div>}
              {biom.body_fat_pct !== null && biom.body_fat_pct !== undefined && <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Body Fat</span><span className="text-gray-700 dark:text-gray-300">{biom.body_fat_pct}%</span></div>}
              {biom.muscle_mass_pct !== null && biom.muscle_mass_pct !== undefined && <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Muscle</span><span className="text-gray-700 dark:text-gray-300">{biom.muscle_mass_pct}%</span></div>}
            </div>
            {biometricsSummary?.benchmark?.body_profile && (
              <div className="mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-700" data-testid="biom-benchmark">
                <p className="text-[10px] uppercase tracking-wide text-gray-400">Current profile</p>
                <p className="text-[11px] font-medium text-teal-700 dark:text-teal-300" data-testid="biom-body-profile">
                  {biometricsSummary.benchmark.body_profile}
                </p>
                {biometricsSummary.benchmark.action_focus?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1" data-testid="biom-action-focus">
                    {biometricsSummary.benchmark.action_focus.slice(0, 2).map((a, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200">
                        {a}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            <p className="text-[10px] text-gray-400 dark:text-gray-500">{biometricsSummary?.count || 0} total records</p>
          </div>
        ) : <EmptyState text="No biometrics yet" />}
      </StatCard>

      {/* Phase 79 — Fitness Assessments summary card. */}
      <StatCard title="Fitness Assessments" icon={Gauge} color="indigo" onClick={can.fitness ? () => quickNav('fitness-assessments') : undefined} testid="overview-card-fitness">
        {!can.fitness ? (
          <RestrictedBody moduleLabel="Fitness Assessments" testid="overview-card-fitness-restricted-body" />
        ) : fit.session_date ? (
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Latest</span><span className="text-gray-700 dark:text-gray-300 truncate ml-2">{fit.session_name || fit.session_date}</span></div>
            {fit.focus_area && <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Focus</span><span className="text-gray-700 dark:text-gray-300 truncate ml-2">{fit.focus_area}</span></div>}
            <div className="flex justify-between text-[10px] pt-1 border-t border-gray-100 dark:border-gray-700">
              <span className="text-emerald-600">{fitnessSummary?.strengths_count || 0} strengths</span>
              <span className="text-amber-600">{fitnessSummary?.improvements_count || 0} to improve</span>
              <span className="text-gray-400">{fitnessSummary?.count || 0} sessions</span>
            </div>
          </div>
        ) : <EmptyState text="No assessments yet" />}
      </StatCard>

      {/* Phase 79 — Nutrition card — athlete-specific plans only. */}
      <StatCard title="Nutrition" icon={Apple} color="lime" onClick={can.nutrition ? () => quickNav('nutrition') : undefined} testid="overview-card-nutrition">
        {!can.nutrition ? (
          <RestrictedBody moduleLabel="Nutrition" testid="overview-card-nutrition-restricted-body" />
        ) : nutri.id ? (
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Active plan</span><span className="text-gray-700 dark:text-gray-300 truncate ml-2">{nutri.title || '—'}</span></div>
            {nutri.plan_type && <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Type</span><span className="text-gray-700 dark:text-gray-300">{nutri.plan_type}</span></div>}
            {nutri.objective && <p className="text-[11px] text-gray-600 dark:text-gray-400 line-clamp-2">{nutri.objective}</p>}
            <p className="text-[10px] text-gray-400 dark:text-gray-500">{nutritionSummary?.count || 0} athlete-specific plans</p>
          </div>
        ) : <EmptyState text="No nutrition plan" />}
      </StatCard>

      {/* Phase 79 — Mental Hub card — athlete-specific items only. */}
      <StatCard title="Mental Hub" icon={Brain} color="pink" onClick={can.mental_hub ? () => quickNav('mental-hub') : undefined} testid="overview-card-mental">
        {!can.mental_hub ? (
          <RestrictedBody moduleLabel="Mental Hub" testid="overview-card-mental-restricted-body" />
        ) : (mentalSummary?.entries_count || 0) > 0 ? (
          <div className="space-y-1.5 text-xs">
            {mentalSummary?.summary_line && (
              <p className="text-[12px] font-medium text-gray-700 dark:text-gray-200" data-testid="mental-summary-line">
                {mentalSummary.summary_line}
              </p>
            )}
            <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Active tasks</span><span className="text-gray-700 dark:text-gray-300 font-semibold" data-testid="mental-active-tasks">{mentalSummary?.active_tasks || 0}</span></div>
            {mentalLatest.title && <div className="text-[11px] text-gray-600 dark:text-gray-400 truncate">Latest: {mentalLatest.title}</div>}
            <p className="text-[10px] text-gray-400 dark:text-gray-500">{mentalSummary?.entries_count || 0} athlete-targeted items</p>
          </div>
        ) : <EmptyState text="No mental hub items" />}
      </StatCard>

      {/* Phase 79 — Career Pathway card. */}
      <StatCard title="Career Pathway" icon={Award} color="orange" onClick={can.career_pathway ? () => quickNav('career-pathway') : undefined} testid="overview-card-career">
        {!can.career_pathway ? (
          <RestrictedBody moduleLabel="Career Pathway" testid="overview-card-career-restricted-body" />
        ) : career.phase_name ? (
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Current phase</span><span className="text-gray-700 dark:text-gray-300 truncate ml-2" data-testid="career-phase-name">{career.phase_name}</span></div>
            <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Status</span>
              <span className="text-gray-700 dark:text-gray-300" data-testid="career-phase-status">{career.status || '—'}</span>
            </div>
            {career.target_date && <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Target</span><span className="text-gray-700 dark:text-gray-300">{career.target_date}</span></div>}
            {typeof career.completion_percentage === 'number' && (
              <div className="flex justify-between"><span className="text-gray-400 dark:text-gray-500">Progress</span><span className="text-gray-700 dark:text-gray-300">{career.completion_percentage}%</span></div>
            )}
            {career.current_milestone && <p className="text-[11px] text-gray-600 dark:text-gray-400 line-clamp-2">{career.current_milestone}</p>}
            {(careerSummary?.total_phases || 0) > 1 && (
              <p className="text-[10px] text-gray-400 dark:text-gray-500">{careerSummary.total_phases} pathway phases on file</p>
            )}
          </div>
        ) : <EmptyState text="No career pathway" />}
      </StatCard>
    </div>
  );
};

// Phase 79 — Filterable + clickable Timeline tab. Filter pills auto-derive
// from the registry-driven event types; clicking a row navigates to the
// event's `link` (the backend computes it per-fetcher).
const TIMELINE_FILTERS = [
  { value: 'all',        label: 'All' },
  { value: 'training',   label: 'Training' },
  { value: 'match',      label: 'Matches' },
  { value: 'pdp',        label: 'PDP' },
  { value: 'fitness',    label: 'Assessments' },
  { value: 'biometrics', label: 'Biometrics' },
  { value: 'injury',     label: 'Injury' },
  { value: 'nutrition',  label: 'Nutrition' },
  { value: 'mental',     label: 'Mental' },
  { value: 'academic',   label: 'Academic' },
  { value: 'career',     label: 'Career' },
  { value: 'documents',  label: 'Documents' },
  { value: 'ai',         label: 'AI' },
];

const TimelineTab = ({ events, navigate, athleteId }) => {
  const [filter, setFilter] = useState('all');
  const filtered = (events || []).filter(e => filter === 'all' || e.type === filter);
  const handleClick = (event) => {
    if (event?.link) navigate(event.link);
  };
  return (
    <div className="space-y-3" data-testid="timeline-tab">
      <div className="flex flex-wrap gap-1.5" data-testid="timeline-filters">
        {TIMELINE_FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`text-[11px] px-2 py-1 rounded-full transition-colors ${
              filter === f.value
                ? 'bg-pfap-green text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            data-testid={`timeline-filter-${f.value}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <EmptyState text="No events for this filter" className="py-12" />
      ) : (
        <div className="space-y-1">
          {filtered.map((event, i) => {
            const style = EVENT_STYLE[event.type] || EVENT_STYLE.training;
            const Icon = style.icon;
            const clickable = !!event.link;
            return (
              <div
                key={i}
                onClick={() => clickable && handleClick(event)}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : -1}
                onKeyDown={(e) => clickable && (e.key === 'Enter' || e.key === ' ') && handleClick(event)}
                className={`flex gap-3 group py-2 px-3 rounded-lg transition-colors ${
                  clickable ? 'hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer' : ''
                }`}
                data-testid={`timeline-event-${event.type}-${i}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${style.bg} border ${style.border}`}>
                  <Icon size={13} className={style.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{event.title}</span>
                    <Badge className={`text-[9px] px-1 py-0 ${style.bg} ${style.color} border ${style.border}`}>{style.label}</Badge>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{event.description}</p>
                </div>
                <span className="text-[10px] text-gray-300 flex-shrink-0 pt-0.5">{event.date}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TrendsTab = ({ ratingTrend, snapshot }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="trends-tab">
    <Card className="bg-white dark:bg-gray-800 shadow-sm">
      <CardHeader className="pb-2 pt-4 px-4"><CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-500">Match Rating Trend</CardTitle></CardHeader>
      <CardContent className="px-4 pb-4">
        {ratingTrend.length >= 2 ? (
          <SafeChart height={180}>
            <LineChart data={ratingTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <YAxis domain={[0, 10]} tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} formatter={(v) => [`${v}/10`, 'Rating']} />
              <Line type="monotone" dataKey="rating" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, fill: '#3b82f6' }} connectNulls />
            </LineChart>
          </SafeChart>
        ) : <EmptyState text="Not enough match data" className="h-[180px]" />}
      </CardContent>
    </Card>
    <Card className="bg-white dark:bg-gray-800 shadow-sm">
      <CardHeader className="pb-2 pt-4 px-4"><CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-500">Development Progress</CardTitle></CardHeader>
      <CardContent className="px-4 pb-4">
        {snapshot?.pdp_progress > 0 ? (
          <div className="space-y-4 pt-2">
            <div className="text-center">
              <span className="text-4xl font-bold text-purple-600">{snapshot.pdp_progress.toFixed(0)}%</span>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Overall PDP Progress</p>
            </div>
            <Progress value={snapshot.pdp_progress} className="h-2" />
            <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500">
              <span>{snapshot?.active_goals_count || 0} active goals</span>
              <span>Career: {snapshot?.career_phase || 'N/A'}</span>
            </div>
          </div>
        ) : <EmptyState text="No PDP data" className="h-[180px]" />}
      </CardContent>
    </Card>
  </div>
);

const StatCard = ({ title, icon: Icon, color, children, onClick, testid }) => {
  const colorMap = { blue: 'text-blue-600', emerald: 'text-emerald-600', purple: 'text-purple-600', red: 'text-red-500', amber: 'text-amber-600', slate: 'text-gray-500 dark:text-gray-500' };
  return (
    <Card data-testid={testid} className={`bg-white dark:bg-gray-800 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md' : ''} transition-all`} onClick={onClick}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon size={15} className={colorMap[color]} />
          <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{title}</h3>
          {onClick && <ChevronRight size={13} className="ml-auto text-gray-300" />}
        </div>
        {children}
      </CardContent>
    </Card>
  );
};

const MiniStat = ({ label, value, highlight }) => (
  <div>
    <p className={`text-xl font-bold ${highlight === 'red' ? 'text-red-500' : 'text-gray-900 dark:text-gray-100'}`}>{value}</p>
    <p className="text-[10px] text-gray-400 dark:text-gray-500">{label}</p>
  </div>
);

const RatingBadge = ({ rating }) => {
  if (!rating) return <span className="text-[10px] text-gray-400 dark:text-gray-500">--</span>;
  const color = rating >= 7.5 ? 'bg-emerald-50 text-emerald-600' : rating >= 6 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600';
  return <Badge className={`text-[10px] px-1.5 py-0 ${color}`}>{rating}/10</Badge>;
};

const EmptyState = ({ text, className = '' }) => (
  <div className={`flex items-center justify-center text-xs text-gray-400 dark:text-gray-500 ${className}`}>{text}</div>
);

// Phase 82 — Restricted empty state for permission-gated Athlete 360 cards.
// Used by OverviewTab when the current user lacks view access to a module.
const RestrictedBody = ({ moduleLabel, testid }) => (
  <div className="flex flex-col items-center justify-center text-center py-3 px-2 gap-1" data-testid={testid || 'restricted-card-body'}>
    <Lock size={14} className="text-gray-400 dark:text-gray-500" />
    <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Restricted</p>
    <p className="text-[10px] text-gray-400 dark:text-gray-500">
      You do not have permission to view {moduleLabel}.
    </p>
  </div>
);

const ReportsTab = ({ reports, athleteId, navigate, user, token, onRefresh }) => {
  const isAdmin = user?.role === 'Admin';
  const canManage = user?.role === 'Admin' || user?.role === 'Staff';

  // Phase 92E.1 — Player Development Reports summary tile. Independent
  // collection from Season/Trial Reports; all three can coexist.
  const [devReports, setDevReports] = useState([]);
  const [devReportsBlocked, setDevReportsBlocked] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/development-reports`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { athlete_id: athleteId },
        });
        setDevReports(Array.isArray(res.data) ? res.data : []);
        setDevReportsBlocked(false);
      } catch {
        setDevReportsBlocked(true); // no module access — hide tile, non-fatal
      }
    })();
  }, [athleteId, token, onRefresh]);
  const devCounts = {
    draft: devReports.filter((r) => r.status === 'draft').length,
    finalized: devReports.filter((r) => r.status === 'finalized').length,
    archived: devReports.filter((r) => r.status === 'archived').length,
  };
  const devRecent = [...devReports].sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)).slice(0, 3);
  const openDevReport = (r) => navigate(r.status === 'draft' ? `/development-reports/${r.id}/edit` : `/development-reports/${r.id}`);

  // Phase 83 — Trial Reports counts/recent for this athlete. Lives alongside
  // Season Reports; independent collection.
  const [trialCounts, setTrialCounts] = useState({ total: 0, counts: { draft: 0, finalized: 0, archived: 0 }, recent: [] });
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/trial-reports/athlete/${athleteId}/counts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setTrialCounts(await res.json());
      } catch { /* non-fatal */ }
    })();
  }, [athleteId, token, onRefresh]);

  const handleDelete = async (reportId) => {
    if (!window.confirm('Delete this report permanently?')) return;
    try {
      const res = await fetch(`${API}/api/reports/season/saved/${reportId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { toast.success('Report deleted'); onRefresh(); }
      else { toast.error('Only admins can delete reports'); }
    } catch { toast.error('Failed to delete'); }
  };

  const handleView = (reportId) => {
    window.open(`/season-report/print?id=${reportId}`, '_blank');
  };

  const handleShare = (reportId) => {
    const url = `${window.location.origin}/season-report/print?shared=${reportId}`;
    navigator.clipboard.writeText(url);
    toast.success('Share link copied to clipboard');
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-3 pt-2" data-testid="reports-tab">
      {/* Phase 92E.1 — Player Development Reports summary tile. */}
      {!devReportsBlocked && (
        <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="dev-reports-tile">
          <CardContent className="p-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Player Development Reports</p>
                <p className="text-sm text-gray-700 dark:text-gray-200 mt-0.5" data-testid="dev-reports-counts">
                  <span className="font-semibold">{devReports.length}</span> total · {devCounts.draft} draft · {devCounts.finalized} finalised · {devCounts.archived} archived
                </p>
              </div>
              <div className="flex gap-2">
                {devReports.length > 0 && (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                          onClick={() => navigate('/development-reports')}
                          data-testid="view-dev-reports-btn">
                    View all
                  </Button>
                )}
                {canManage && (
                  <Button size="sm" className="bg-pfap-green hover:bg-pfap-green/90 text-white h-7 px-3 text-xs"
                          onClick={() => navigate('/development-reports')}
                          data-testid="new-dev-report-btn">
                    <Plus size={12} className="mr-1" /> New Development Report
                  </Button>
                )}
              </div>
            </div>
            {devRecent.length > 0 && (
              <div className="mt-2 space-y-1">
                {devRecent.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => openDevReport(r)}
                    className="w-full flex items-center justify-between text-[11px] px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    data-testid={`dev-report-recent-${r.id}`}
                  >
                    <span className="truncate text-gray-700 dark:text-gray-300">{r.report_title}</span>
                    <span className="text-gray-400 ml-2 capitalize">{r.status}</span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Phase 83 — Trial Reports summary tile. Independent from Season
          Reports; both report types can coexist for the same athlete. */}
      <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid="trial-reports-tile">
        <CardContent className="p-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Trial Reports</p>
              <p className="text-sm text-gray-700 dark:text-gray-200 mt-0.5" data-testid="trial-reports-counts">
                <span className="font-semibold">{trialCounts.total}</span> total · {trialCounts.counts?.draft || 0} draft · {trialCounts.counts?.finalized || 0} finalised · {trialCounts.counts?.archived || 0} archived
              </p>
            </div>
            <div className="flex gap-2">
              {trialCounts.total > 0 && (
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                        onClick={() => navigate(`/trial-reports?athlete=${athleteId}`)}
                        data-testid="view-trial-reports-btn">
                  View all
                </Button>
              )}
              {canManage && (
                <Button size="sm" className="bg-pfap-green hover:bg-pfap-green/90 text-white h-7 px-3 text-xs"
                        onClick={() => navigate('/trial-reports')}
                        data-testid="new-trial-report-btn">
                  <Plus size={12} className="mr-1" /> New Trial
                </Button>
              )}
            </div>
          </div>
          {trialCounts.recent?.length > 0 && (
            <div className="mt-2 space-y-1">
              {trialCounts.recent.slice(0, 3).map(r => (
                <button
                  key={r.id}
                  onClick={() => navigate(`/trial-reports/${r.id}`)}
                  className="w-full flex items-center justify-between text-[11px] px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  data-testid={`trial-report-recent-${r.id}`}
                >
                  <span className="truncate text-gray-700 dark:text-gray-300">{r.title}</span>
                  <span className="text-gray-400 ml-2">{r.status}</span>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between pt-1">
        <p className="text-xs text-gray-500 dark:text-gray-400">Season Reports — {reports.length} saved</p>
        {canManage && (
          <Button
            size="sm"
            className="bg-pfap-green hover:bg-pfap-green/90 text-white h-7 px-3 text-xs"
            onClick={() => navigate('/season-reports')}
            data-testid="create-new-report-btn"
          >
            <Plus size={12} className="mr-1" /> New Season Report
          </Button>
        )}
      </div>
      {reports.length === 0 ? (
        <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
          <CardContent className="py-10 text-center">
            <FileText size={28} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-xs text-gray-400 dark:text-gray-500">No reports saved yet</p>
            {canManage && <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">Generate a season report to see it here</p>}
          </CardContent>
        </Card>
      ) : (
        reports.map((r) => (
          <Card key={r.id} className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700" data-testid={`report-${r.id}`}>
            <CardContent className="p-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-pfap-green/10 dark:bg-pfap-green/20 flex items-center justify-center flex-shrink-0">
                  <FileText size={16} className="text-pfap-green dark:text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Season {r.season}</p>
                  <div className="flex items-center gap-3 text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                    <span className="flex items-center gap-1"><Clock size={10} /> {formatDate(r.created_at)}</span>
                    <span>By {r.created_by_name || 'Staff'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-pfap-green dark:text-emerald-400 hover:bg-pfap-green/10" onClick={() => handleView(r.id)} data-testid={`view-report-${r.id}`}>
                    <ExternalLink size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => handleShare(r.id)} data-testid={`share-report-${r.id}`}>
                    <Link2 size={14} />
                  </Button>
                  {isAdmin && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => handleDelete(r.id)} data-testid={`delete-report-${r.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// AI Decisions Tab — Admin/Staff only. Lists saved Decision Engine outputs
// tied to this athlete. Supports delete (creator or Admin).
// ---------------------------------------------------------------------------
const AIDecisionsTab = ({ athleteId, token, user }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({}); // id -> edited text
  const [fbBusy, setFbBusy] = useState(null); // id currently patching

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/intelligence/saved-decisions`, {
        params: { player_id: athleteId, limit: 50 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load AI decisions');
    } finally {
      setLoading(false);
    }
  }, [athleteId, token]);

  useEffect(() => { load(); }, [load]);

  const canDelete = (d) => user?.role === 'Admin' || d.saved_by?.id === user?.id;

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this saved decision?')) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/api/intelligence/saved-decisions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success('Decision deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const patchFeedback = async (d, patch) => {
    setFbBusy(d.id);
    const payload = {
      status: patch.status ?? (d.status === 'pending' ? 'followed' : d.status),
      outcome_note: patch.outcome_note !== undefined ? patch.outcome_note : d.outcome_note,
      outcome_rating: patch.outcome_rating !== undefined ? patch.outcome_rating : d.outcome_rating,
    };
    // Strip nulls so server validation is happy — only include optional fields if set.
    const body = { status: payload.status };
    if (payload.outcome_note != null) body.outcome_note = payload.outcome_note;
    if (payload.outcome_rating != null) body.outcome_rating = payload.outcome_rating;
    try {
      const r = await axios.patch(
        `${API}/api/intelligence/saved-decisions/${d.id}/feedback`,
        body,
        { headers: authHeaders },
      );
      setItems((xs) => xs.map((x) => (x.id === d.id ? r.data : x)));
      toast.success('Feedback saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setFbBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-sm text-gray-500" data-testid="ai-decisions-loading">
        Loading saved decisions…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card data-testid="ai-decisions-empty">
        <CardContent className="py-10 text-center text-sm text-gray-500">
          No AI decisions saved for this player yet.<br />
          Use the Decision Engine and click <span className="font-medium">Save</span> to add one here.
        </CardContent>
      </Card>
    );
  }

  const borderForStatus = (s) =>
    s === 'followed' ? 'border-l-emerald-500'
    : s === 'ignored' ? 'border-l-amber-500'
    : 'border-l-indigo-500';

  const statusBadge = (s) => {
    if (s === 'followed') return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]" data-testid="status-badge-followed">Followed</Badge>;
    if (s === 'ignored') return <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]" data-testid="status-badge-ignored">Ignored</Badge>;
    return <Badge variant="outline" className="text-[10px] text-gray-500" data-testid="status-badge-pending">Pending</Badge>;
  };

  return (
    <div className="space-y-3" data-testid="ai-decisions-list">
      {items.map((d) => {
        const isOpen = !!expanded[d.id];
        const noteValue = noteDrafts[d.id] !== undefined ? noteDrafts[d.id] : (d.outcome_note || '');
        const isBusy = fbBusy === d.id;
        return (
          <Card key={d.id} className={`border-l-4 ${borderForStatus(d.status)}`} data-testid={`ai-decision-${d.id}`}>
            <CardContent className="py-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100" data-testid={`ai-decision-summary-${d.id}`}>
                      {d.decision?.decision_summary || '—'}
                    </p>
                    {statusBadge(d.status)}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {new Date(d.saved_at).toLocaleString()} · {d.saved_by?.full_name || '—'} ({d.saved_by?.role || '—'})
                    {d.source ? <> · <Badge variant="outline" className="text-[10px] ml-1">{d.source}</Badge></> : null}
                    {d.updated_at ? <> · <span className="italic">updated {new Date(d.updated_at).toLocaleString()}</span></> : null}
                  </p>
                  {!isOpen && d.decision?.reasoning && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1.5 line-clamp-2">
                      {d.decision.reasoning}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpanded((e) => ({ ...e, [d.id]: !isOpen }))}
                    data-testid={`ai-decision-toggle-${d.id}`}
                  >
                    {isOpen ? 'Collapse' : 'Expand'}
                  </Button>
                  {canDelete(d) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(d.id)}
                      disabled={busyId === d.id}
                      className="text-red-600 hover:text-red-700"
                      data-testid={`ai-decision-delete-${d.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Feedback row — always visible so coaches can mark in 1-2 clicks */}
              <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-dashed" data-testid={`feedback-row-${d.id}`}>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant={d.status === 'followed' ? 'default' : 'outline'}
                    disabled={isBusy}
                    onClick={() => patchFeedback(d, { status: 'followed' })}
                    className={d.status === 'followed' ? 'bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2 text-xs' : 'h-7 px-2 text-xs'}
                    data-testid={`feedback-followed-${d.id}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Followed
                  </Button>
                  <Button
                    size="sm"
                    variant={d.status === 'ignored' ? 'default' : 'outline'}
                    disabled={isBusy}
                    onClick={() => patchFeedback(d, { status: 'ignored' })}
                    className={d.status === 'ignored' ? 'bg-amber-500 hover:bg-amber-600 text-white h-7 px-2 text-xs' : 'h-7 px-2 text-xs'}
                    data-testid={`feedback-ignored-${d.id}`}
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1" /> Ignored
                  </Button>
                </div>

                <div className="flex items-center gap-1" data-testid={`rating-group-${d.id}`}>
                  {[
                    { key: 'positive', icon: ThumbsUp, active: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
                    { key: 'neutral', icon: Minus, active: 'bg-gray-100 text-gray-700 border-gray-300' },
                    { key: 'negative', icon: ThumbsDown, active: 'bg-red-100 text-red-700 border-red-200' },
                  ].map(({ key, icon: Icon, active }) => {
                    const selected = d.outcome_rating === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        disabled={isBusy}
                        onClick={() => patchFeedback(d, { outcome_rating: selected ? null : key, status: d.status === 'pending' ? 'followed' : d.status })}
                        className={`h-7 w-7 inline-flex items-center justify-center rounded-md border text-xs transition-colors disabled:opacity-50 ${
                          selected ? active : 'border-gray-200 dark:border-gray-700 text-gray-400 hover:text-gray-700'
                        }`}
                        title={key}
                        data-testid={`feedback-rating-${key}-${d.id}`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </button>
                    );
                  })}
                </div>

                <div className="flex-1 min-w-[200px] flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Outcome note (optional)"
                    value={noteValue}
                    disabled={isBusy}
                    onChange={(e) => setNoteDrafts((n) => ({ ...n, [d.id]: e.target.value }))}
                    onBlur={() => {
                      const next = noteDrafts[d.id];
                      if (next !== undefined && next !== (d.outcome_note || '')) {
                        patchFeedback(d, {
                          outcome_note: next || null,
                          status: d.status === 'pending' ? 'followed' : d.status,
                        });
                      }
                    }}
                    className="flex-1 h-7 px-2 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-transparent focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    data-testid={`feedback-note-${d.id}`}
                  />
                  {isBusy && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
                </div>
              </div>

              {isOpen && (
                <div className="pt-2 border-t space-y-3 text-sm">
                  {d.query && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Query</div>
                      <p className="text-gray-700 dark:text-gray-300">{d.query}</p>
                    </div>
                  )}
                  {d.decision?.reasoning && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Reasoning</div>
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{d.decision.reasoning}</p>
                    </div>
                  )}
                  {d.decision?.risk_notes && (
                    <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 p-2">
                      <div className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-0.5">Risk Notes</div>
                      <p className="text-amber-900 dark:text-amber-200 whitespace-pre-wrap leading-relaxed">{d.decision.risk_notes}</p>
                    </div>
                  )}
                  {d.decision?.alternative_option && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">Alternative</div>
                      <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">{d.decision.alternative_option}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default Athlete360Dashboard;
